import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Btn } from '../primitives/Btn';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useWindowSize } from '../../hooks/useWindowSize';
import { ACCENT, BORDER, EASE, TEXT_PRIMARY, TEXT_SECONDARY } from '../../tokens';
import type { View } from '../../types';

/** Section ids tracked by the scroll spy, in document order. */
const SECTION_IDS = ['problem', 'solution', 'process', 'proof', 'included'] as const;

/** A nav entry is either an anchor to a section or a button that swaps view. */
type NavEntry =
  | { label: string; href: string; onClick?: never }
  | { label: string; href?: never; onClick: () => void };

/** Geometry of the sliding pill that sits behind the active nav link. */
interface PillStyle {
  left: number;
  width: number;
  top: number;
  height: number;
  opacity: number;
}

/** How far scroll has progressed between two adjacent sections. */
interface ScrollProgress {
  current: number;
  next: number;
  /** 0 at the section midpoint, 1 at its end — drives pill interpolation. */
  t: number;
}

const INACTIVE_LINK = 'rgba(245,245,245,0.55)';

interface NavbarProps {
  ctaHref: string;
  onCta: (event: MouseEvent<HTMLAnchorElement>) => void;
  onAbout: () => void;
  onHome: () => void;
  currentView: View;
}

export function Navbar({ ctaHref, onCta, onAbout, onHome, currentView }: NavbarProps) {
  const [activeSection, setActiveSection] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  const { width } = useWindowSize();

  const linksContainerRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<(HTMLElement | null)[]>([]);
  const [pillStyle, setPillStyle] = useState<PillStyle>({
    left: 0,
    width: 0,
    top: 0,
    height: 0,
    opacity: 0,
  });
  const [scrollProgress, setScrollProgress] = useState<ScrollProgress>({ current: 0, next: 0, t: 0 });

  const links: readonly NavEntry[] = [
    { label: 'Problem', href: '#problem' },
    { label: 'Solution', href: '#solution' },
    { label: 'Process', href: '#process' },
    { label: 'Proof', href: '#proof' },
    { label: 'Included', href: '#included' },
    { label: 'About', onClick: onAbout },
  ];

  // Scroll spy: whichever section centre sits closest to the viewport centre
  // wins. Progress through that section drives the pill interpolation.
  useEffect(() => {
    if (currentView !== 'home') return;

    let rafId = 0;

    const update = (): void => {
      const viewportCenter = window.scrollY + window.innerHeight / 2;
      let bestId = '';
      let bestDist = Infinity;
      let bestProgress = 0;
      let bestIdx = -1;

      SECTION_IDS.forEach((id, idx) => {
        const el = document.getElementById(id);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const elTop = window.scrollY + rect.top;
        const elCenter = elTop + rect.height / 2;
        const dist = Math.abs(elCenter - viewportCenter);

        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
          bestIdx = idx;
          bestProgress = Math.min(1, Math.max(0, (viewportCenter - elTop) / rect.height));
        }
      });

      if (!bestId) return;

      setActiveSection(bestId);
      setScrollProgress({
        current: bestIdx,
        next: bestIdx + 1 < SECTION_IDS.length ? bestIdx + 1 : bestIdx,
        // Only start shifting toward the next link past the halfway mark.
        t: Math.max(0, (bestProgress - 0.5) * 2),
      });
    };

    const onScroll = (): void => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [currentView]);

  // Position the sliding pill behind the active link.
  useEffect(() => {
    if (isMobile) {
      setPillStyle((s) => ({ ...s, opacity: 0 }));
      return;
    }

    const updatePill = (): void => {
      const container = linksContainerRef.current;
      if (!container) return;

      const activeIdx =
        currentView === 'about'
          ? links.findIndex((l) => l.label === 'About')
          : activeSection
            ? links.findIndex((l) => l.label.toLowerCase() === activeSection)
            : -1;

      const activeEl = activeIdx >= 0 ? linkRefs.current[activeIdx] : null;
      if (!activeEl) {
        setPillStyle((s) => ({ ...s, opacity: 0 }));
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();

      let targetLeft = activeRect.left - containerRect.left;
      let targetWidth = activeRect.width;

      // Past a section's midpoint, ease the pill toward the next link.
      if (currentView !== 'about' && scrollProgress.t > 0) {
        const nextEl = linkRefs.current[scrollProgress.next];
        if (nextEl) {
          const nextRect = nextEl.getBoundingClientRect();
          const t = scrollProgress.t;
          targetLeft = activeRect.left + (nextRect.left - activeRect.left) * t - containerRect.left;
          targetWidth = activeRect.width + (nextRect.width - activeRect.width) * t;
        }
      }

      setPillStyle({
        left: targetLeft,
        width: targetWidth,
        top: activeRect.top - containerRect.top,
        height: activeRect.height,
        opacity: 1,
      });
    };

    // Let layout settle before measuring.
    const timer = setTimeout(updatePill, 50);
    return () => clearTimeout(timer);
  }, [activeSection, currentView, isMobile, width, scrollProgress.t, scrollProgress.next]);

  const logoClick = (): void => {
    if (currentView === 'about') onHome();
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // From the About view an in-page anchor has nothing to scroll to, so swap
  // back to Home first and scroll once the section has actually rendered.
  const handleNavClick = (event: React.MouseEvent, href: string): void => {
    if (currentView !== 'about') return;

    event.preventDefault();
    onHome();

    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = document.getElementById(href.replace('#', ''));
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 80);
    });
  };

  const ctaAnimation = activeSection === 'process' ? 'ctaGlowPulse 2s ease-in-out infinite' : undefined;

  return (
    <header role="banner">
      <nav
        style={{
          position: 'fixed',
          top: 18,
          left: 0,
          right: 0,
          zIndex: 9000,
          display: 'flex',
          justifyContent: 'center',
          padding: '0 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 12 : 32,
            padding: '10px 16px',
            borderRadius: 100,
            background: 'rgba(13,13,13,0.92)',
            backdropFilter: 'blur(22px)',
            border: `1px solid ${BORDER}`,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'center',
            transition: `all 0.4s ${EASE}`,
            position: 'relative',
          }}
        >
          <span
            onClick={logoClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 900,
              fontSize: '0.95rem',
              letterSpacing: '-0.03em',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            <img
              src="/logos/augment-os-logo.png"
              alt="Augment OS"
              style={{ width: isMobile ? 22 : 34, height: isMobile ? 22 : 34, borderRadius: 8, display: 'block' }}
            />
            Augment OS
          </span>

          {!isMobile && (
            <div ref={linksContainerRef} style={{ display: 'flex', gap: 4, alignItems: 'center', position: 'relative' }}>
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: pillStyle.left,
                  width: pillStyle.width,
                  top: pillStyle.top,
                  height: pillStyle.height,
                  borderRadius: 100,
                  background: 'rgba(255,107,0,0.12)',
                  border: '1px solid rgba(255,107,0,0.25)',
                  transition: `left 0.35s ${EASE}, width 0.35s ${EASE}, opacity 0.2s ease`,
                  pointerEvents: 'none',
                  zIndex: 0,
                  opacity: pillStyle.opacity,
                }}
              />

              {links.map((link, i) => {
                const isActive =
                  link.onClick !== undefined
                    ? currentView === 'about'
                    : activeSection === link.label.toLowerCase();

                const baseStyle: React.CSSProperties = {
                  position: 'relative',
                  zIndex: 1,
                  color: isActive ? ACCENT : INACTIVE_LINK,
                  fontSize: '0.86rem',
                  textDecoration: 'none',
                  fontWeight: isActive ? 600 : 500,
                  padding: '6px 14px',
                  borderRadius: 100,
                  background: 'transparent',
                  transition: `all 0.25s ${EASE}`,
                };

                const onEnter = (e: React.MouseEvent<HTMLElement>): void => {
                  if (isActive) return;
                  e.currentTarget.style.color = TEXT_PRIMARY;
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                };

                const onLeave = (e: React.MouseEvent<HTMLElement>): void => {
                  if (isActive) return;
                  e.currentTarget.style.color = INACTIVE_LINK;
                  e.currentTarget.style.background = 'transparent';
                };

                if (link.onClick) {
                  return (
                    <button
                      key={link.label}
                      ref={(el) => {
                        linkRefs.current[i] = el;
                      }}
                      onClick={link.onClick}
                      style={{ ...baseStyle, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      onMouseEnter={onEnter}
                      onMouseLeave={onLeave}
                    >
                      {link.label}
                    </button>
                  );
                }

                return (
                  <a
                    key={link.label}
                    ref={(el) => {
                      linkRefs.current[i] = el;
                    }}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    style={baseStyle}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                  >
                    {link.label}
                  </a>
                );
              })}
            </div>
          )}

          {!isMobile && (
            <Btn href={ctaHref} onClick={onCta} style={{ padding: '9px 20px', fontSize: '0.83rem', animation: ctaAnimation }}>
              Get My Free Competitor Scan →
            </Btn>
          )}

          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Btn
                href={ctaHref}
                onClick={(e) => {
                  setMobileOpen(false);
                  onCta(e);
                }}
                style={{ padding: '8px 14px', fontSize: '0.72rem', whiteSpace: 'nowrap', animation: ctaAnimation }}
              >
                Free Competitor Scan →
              </Btn>
              <button
                onClick={() => setMobileOpen((o) => !o)}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: TEXT_PRIMARY,
                  cursor: 'pointer',
                  padding: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  {mobileOpen ? (
                    <>
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </>
                  ) : (
                    <>
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          )}

          {isMobile && mobileOpen && (
            <div
              style={{
                position: 'absolute',
                top: 60,
                left: 0,
                right: 0,
                background: 'rgba(13,13,13,0.97)',
                backdropFilter: 'blur(22px)',
                border: `1px solid ${BORDER}`,
                borderRadius: 16,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                zIndex: 9001,
              }}
            >
              {links.map((link) => {
                const mobileStyle: React.CSSProperties = {
                  color: link.onClick && currentView === 'about' ? ACCENT : TEXT_SECONDARY,
                  fontSize: '0.9rem',
                  textDecoration: 'none',
                  padding: '12px 16px',
                  borderRadius: 8,
                  fontWeight: 500,
                  transition: 'all 0.2s',
                };

                const onEnter = (e: React.MouseEvent<HTMLElement>): void => {
                  e.currentTarget.style.background = 'rgba(255,107,0,0.1)';
                  e.currentTarget.style.color = ACCENT;
                };

                const onLeave = (e: React.MouseEvent<HTMLElement>): void => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color =
                    link.onClick && currentView === 'about' ? ACCENT : TEXT_SECONDARY;
                };

                if (link.onClick) {
                  const handler = link.onClick;
                  return (
                    <button
                      key={link.label}
                      onClick={() => {
                        handler();
                        setMobileOpen(false);
                      }}
                      style={{
                        ...mobileStyle,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        width: '100%',
                      }}
                      onMouseEnter={onEnter}
                      onMouseLeave={onLeave}
                    >
                      {link.label}
                    </button>
                  );
                }

                return (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => {
                      handleNavClick(e, link.href);
                      setMobileOpen(false);
                    }}
                    style={mobileStyle}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                  >
                    {link.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
